/**
 * Mona proof blocks — stepped count-up + desktop L→R pop/count sequencing.
 * Pop visuals reuse StoreReveal (`data-animate="pop"` / `is-animated`).
 */

/**
 * @returns {boolean}
 */
function allowMotion() {
  if (window.Shopify?.designMode) return true;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return false;
  }
  // Phones are lite only to spare the page's loops; these boxes opt back in.
  if (window.__perf && window.__perf.allowMotion === false && !document.documentElement.hasAttribute('data-perf-phone')) return false;
  return true;
}

/**
 * @returns {boolean}
 */
function isDesktop() {
  return typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1000px)').matches;
}

/**
 * @param {number} value
 * @param {number} decimals
 * @returns {number}
 */
function roundTo(value, decimals) {
  const factor = 10 ** decimals;
  return Math.round(Math.max(0, value) * factor) / factor;
}

/**
 * Fixed decimal places so the text doesn't flicker between "1" and "1.0".
 *
 * @param {number} value
 * @param {number} decimals
 * @returns {string}
 */
function formatStepped(value, decimals) {
  return roundTo(value, decimals).toFixed(decimals);
}

/**
 * @param {number} step
 * @returns {number}
 */
function decimalsForStep(step) {
  if (!Number.isFinite(step) || step >= 1) return 0;
  const text = String(step);
  const parts = text.split('.');
  return parts[1] ? parts[1].length : 0;
}

/**
 * @typedef {object} ReelColumn
 * @property {number} place
 * @property {boolean} leading
 * @property {boolean} lowest
 * @property {boolean} active
 * @property {HTMLElement} col
 * @property {HTMLElement} strip
 */

/**
 * @typedef {object} Reel
 * @property {ReelColumn[]} columns
 * @property {string} finalText
 * @property {number} decimals
 * @property {number} target
 */

/** @type {WeakMap<HTMLElement, Reel>} */
const reels = new WeakMap();

/** @type {WeakMap<HTMLElement, number>} */
const reelFrames = new WeakMap();

/**
 * @param {number} value
 * @param {number} place
 * @param {boolean} lowest
 * @returns {number}
 */
function reelOffset(value, place, lowest) {
  const safe = Math.max(0, value);
  if (lowest) {
    const offset = (safe / place) % 10;
    return offset < 0 ? offset + 10 : offset;
  }

  const digit = Math.floor(safe / place + 1e-8) % 10;
  const lowerPos = (safe / (place / 10)) % 10;
  if (lowerPos >= 9) return digit + (lowerPos - 9);
  return digit;
}

/**
 * @param {HTMLElement} parent
 * @param {number} place
 * @param {boolean} leading
 * @param {boolean} lowest
 * @returns {ReelColumn}
 */
function createReelColumn(parent, place, leading, lowest) {
  const col = document.createElement('span');
  col.className = 'mona-proof__reel-col';
  if (leading) col.classList.add('is-pending');

  const strip = document.createElement('span');
  strip.className = 'mona-proof__reel-strip';
  for (const glyph of '01234567890') {
    const cell = document.createElement('span');
    cell.textContent = glyph;
    strip.appendChild(cell);
  }
  col.appendChild(strip);
  parent.appendChild(col);

  return { place, leading, lowest, active: !leading, col, strip };
}

/**
 * Equal-width digit reels. The number rolls continuously; width only grows
 * when a new digit place is reached.
 *
 * @param {HTMLElement} counterEl
 * @param {number} target
 * @param {number} decimals
 * @returns {Reel}
 */
function mountReel(counterEl, target, decimals) {
  const finalText = formatStepped(target, decimals);
  const [intPart, fracPart = ''] = finalText.split('.');
  const root = document.createElement('span');
  root.className = 'mona-proof__reel';
  root.setAttribute('aria-hidden', 'true');

  const strut = document.createElement('span');
  strut.className = 'mona-proof__reel-strut';
  strut.textContent = '0';
  root.appendChild(strut);

  /** @type {ReelColumn[]} */
  const columns = [];
  const intDigits = intPart.split('');
  intDigits.forEach((_, index) => {
    const place = 10 ** (intDigits.length - 1 - index);
    columns.push(createReelColumn(root, place, index < intDigits.length - 1, fracPart === '' && index === intDigits.length - 1));
  });

  if (fracPart) {
    const point = document.createElement('span');
    point.className = 'mona-proof__reel-point';
    point.textContent = '.';
    root.appendChild(point);
    const fracDigits = fracPart.split('');
    fracDigits.forEach((_, index) => {
      columns.push(createReelColumn(root, 10 ** -(index + 1), false, index === fracDigits.length - 1));
    });
  }

  const sr = document.createElement('span');
  sr.className = 'mona-proof__value-sr';
  sr.textContent = finalText;
  counterEl.replaceChildren(sr, root);

  /** @type {Reel} */
  const reel = { columns, finalText, decimals, target };
  reels.set(counterEl, reel);
  setReelValue(reel, 0);
  return reel;
}

/**
 * @param {Reel} reel
 * @param {number} value
 */
function setReelValue(reel, value) {
  reel.columns.forEach((column) => {
    const offset = reelOffset(value, column.place, column.lowest);
    if (column.leading) {
      const active = offset > 0.001 || value + 1e-8 >= column.place;
      if (active !== column.active) {
        column.active = active;
        column.col.classList.toggle('is-pending', !active);
      }
    }
    column.strip.style.setProperty('--reel', offset.toFixed(4));
  });
}

/**
 * Land on the exact target glyphs.
 *
 * @param {Reel} reel
 */
function snapReel(reel) {
  const digits = reel.finalText.replace('.', '').split('');
  digits.forEach((glyph, index) => {
    const column = reel.columns[index];
    if (!column) return;
    column.active = true;
    column.col.classList.remove('is-pending');
    column.strip.style.setProperty('--reel', String(Number(glyph) || 0));
  });
}

/**
 * Smooth linear roll from 0 → target over durationMs.
 *
 * @param {Element} counterEl
 * @returns {Promise<void>}
 */
function animateSteppedCounter(counterEl) {
  return new Promise((resolve) => {
    if (!(counterEl instanceof HTMLElement)) {
      resolve();
      return;
    }

    const raw = (counterEl.getAttribute('data-target-number') || '').trim();
    if (!raw) {
      resolve();
      return;
    }

    const target = Number.parseFloat(raw.replace(/,/g, ''));
    const step = Number.parseFloat(counterEl.getAttribute('data-grow-step') || '1');
    const durationMs = Number.parseFloat(counterEl.getAttribute('data-count-duration-ms') || '2000');

    if (!Number.isFinite(target) || !Number.isFinite(step) || step <= 0 || !Number.isFinite(durationMs)) {
      counterEl.textContent = raw;
      resolve();
      return;
    }

    const decimals = Math.max(decimalsForStep(step), (raw.split('.')[1] || '').length);
    const reel = reels.get(counterEl) || mountReel(counterEl, target, decimals);
    const previousFrame = reelFrames.get(counterEl);
    if (previousFrame) cancelAnimationFrame(previousFrame);

    if (durationMs <= 0) {
      snapReel(reel);
      resolve();
      return;
    }

    /** @type {number | null} */
    let startTime = null;

    /** @param {number} timestamp */
    const frame = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min(1, (timestamp - startTime) / durationMs);
      if (progress >= 1) {
        snapReel(reel);
        reelFrames.delete(counterEl);
        resolve();
        return;
      }
      setReelValue(reel, target * progress);
      reelFrames.set(counterEl, requestAnimationFrame(frame));
    };

    reelFrames.set(counterEl, requestAnimationFrame(frame));
  });
}

/**
 * @param {HTMLElement} element
 * @param {number} popDurationMs
 */
function popElement(element, popDurationMs) {
  element.style.setProperty('--store-reveal-duration', `${Math.max(0, popDurationMs)}ms`);
  element.style.willChange = 'transform, opacity';
  element.classList.add('is-animated');
  element.dispatchEvent(new CustomEvent('store-reveal:shown', { bubbles: true }));
}

/**
 * @param {HTMLElement} sectionEl
 */
function initMonaProofSection(sectionEl) {
  if (!(sectionEl instanceof HTMLElement)) return;
  if (sectionEl.dataset.monaProofInit) return;
  sectionEl.dataset.monaProofInit = 'true';

  const cards = Array.from(sectionEl.querySelectorAll('[data-mona-proof-card]'));
  const social = sectionEl.querySelector('[data-mona-proof-social]');
  const socialPops = social
    ? Array.from(social.querySelectorAll('[data-animate="pop"]'))
    : [];
  const popDurationMs = Number(sectionEl.dataset.popDurationMs || 700);
  const overlapRatio = Math.min(1, Math.max(0, Number(sectionEl.dataset.animationOverlap || 0)));
  const motionOk = allowMotion();
  const desktop = isDesktop();

  const counters = cards
    .map((card) => card.querySelector('[data-mona-proof-number]'))
    .filter(Boolean);

  if (!motionOk) {
    counters.forEach((counterEl) => {
      const raw = (counterEl.getAttribute('data-target-number') || '').trim();
      if (raw) counterEl.textContent = raw;
    });
    return;
  }

  counters.forEach((counterEl) => {
    if (!(counterEl instanceof HTMLElement)) return;
    const raw = (counterEl.getAttribute('data-target-number') || '').trim();
    if (!raw) return;
    const target = Number.parseFloat(raw.replace(/,/g, ''));
    const step = Number.parseFloat(counterEl.getAttribute('data-grow-step') || '1');
    if (!Number.isFinite(target) || !Number.isFinite(step) || step <= 0) return;
    const decimals = Math.max(decimalsForStep(step), (raw.split('.')[1] || '').length);
    mountReel(counterEl, target, decimals);
  });

  /** @type {WeakSet<Element>} */
  const countedCards = new WeakSet();
  /** @type {WeakSet<Element>} */
  const intentionallyPopped = new WeakSet();

  /**
   * @param {Element} card
   * @returns {Promise<void>}
   */
  async function countCard(card) {
    if (countedCards.has(card)) return;
    countedCards.add(card);
    const counterEl = card.querySelector('[data-mona-proof-number]');
    if (!counterEl) return;
    await animateSteppedCounter(counterEl);
  }

  if (!desktop) {
    cards.forEach((card) => {
      card.addEventListener(
        'store-reveal:shown',
        () => {
          window.setTimeout(() => {
            countCard(card);
          }, popDurationMs);
        },
        { once: true }
      );
    });
    return;
  }

  // Desktop: reclaim StoreReveal auto-shows, then sequence pop → count L→R.
  const reclaim = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !sectionEl.contains(target)) return;
    if (intentionallyPopped.has(target)) return;

    const card = target.closest('[data-mona-proof-card]');
    if (card && sectionEl.contains(card) && target === card) {
      card.classList.remove('is-animated');
      return;
    }

    if (social && social.contains(target) && target.hasAttribute('data-animate')) {
      target.classList.remove('is-animated');
    }
  };

  sectionEl.addEventListener('store-reveal:shown', reclaim, true);

  let hasSequenced = false;

  /**
   * @param {Element} card
   * @returns {number}
   */
  function cardSlotMs(card) {
    const counter = card.querySelector('[data-mona-proof-number]');
    const countMs = Number(counter?.getAttribute('data-count-duration-ms') || 0);
    return popDurationMs + Math.max(0, countMs);
  }

  const runSequence = () => {
    if (hasSequenced) return;
    hasSequenced = true;

    let cursor = 0;
    cards.forEach((card) => {
      const at = cursor;
      window.setTimeout(() => {
        card.classList.remove('is-animated');
        void card.offsetWidth;
        intentionallyPopped.add(card);
        popElement(card, popDurationMs);
        window.setTimeout(() => {
          countCard(card);
        }, popDurationMs);
      }, at);
      cursor += cardSlotMs(card) * (1 - overlapRatio);
    });

    socialPops.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const at = cursor;
      window.setTimeout(() => {
        item.classList.remove('is-animated');
        void item.offsetWidth;
        intentionallyPopped.add(item);
        popElement(item, popDurationMs);
      }, at);
      cursor += popDurationMs * (1 - overlapRatio);
    });
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || hasSequenced) return;
        observer.disconnect();
        window.requestAnimationFrame(() => {
          cards.forEach((card) => card.classList.remove('is-animated'));
          socialPops.forEach((el) => el.classList.remove('is-animated'));
          runSequence();
        });
      });
    },
    { threshold: 0.25 }
  );

  observer.observe(sectionEl);
}

document.querySelectorAll('[data-mona-proof-section]').forEach(initMonaProofSection);

document.addEventListener('shopify:section:load', (event) => {
  const root = /** @type {HTMLElement | null} */ (event.target);
  if (!root) return;
  if (root.matches?.('[data-mona-proof-section]')) initMonaProofSection(root);
  root.querySelectorAll?.('[data-mona-proof-section]').forEach(initMonaProofSection);
});
